import { notification } from 'antd'
import i18next from 'i18next'
import { makeAutoObservable } from 'mobx'

import { USER_PERMISSION } from 'shared/types/types'

import { employeeApi } from '../api'
import { EmployeeKeys } from '../data'
import { UpdateUserPvzRolesRequest } from '../types/typesDTO'
import { ViewEmployeeUiStore } from './ViewEmployeeUiStore'

const getKey = (key: string) => `${EmployeeKeys.employeeModule}.api.${key}`

export class ViewEmployeeApiStore {
  constructor(protected readonly uiStore: ViewEmployeeUiStore) {
    makeAutoObservable(this)
  }

  async addEmployee() {
    try {
      if (this.uiStore.viewPvzUiStore.selectedPvz?.id) {
        const { token } = await employeeApi.addEmployee({
          lastName: this.uiStore.lastName,
          firstName: this.uiStore.firstName,
          snils: Number(this.uiStore.snils),
          pickPointId: this.uiStore.viewPvzUiStore.selectedPvz.id,
          phone: this.uiStore.phoneModel.fullPhone,
          country: this.uiStore.phoneModel.selectedCountry.country,
          permission: USER_PERMISSION.PERM_EMPLOYEE,
        })

        this.uiStore.setToken(token)

        return true
      }
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('addEmployeeError')),
        description: (error as Error).message,
      })
    }
  }

  async verifyEmployee() {
    try {
      if (this.uiStore.viewPvzUiStore.selectedPvz?.id) {
        const userId = await employeeApi.verifyEmployee({
          lastName: this.uiStore.lastName,
          firstName: this.uiStore.firstName,
          snils: Number(this.uiStore.snils),
          pickPointId: this.uiStore.viewPvzUiStore.selectedPvz.id,
          phone: this.uiStore.phoneModel.fullPhone,
          country: this.uiStore.phoneModel.selectedCountry.country,
          permission: USER_PERMISSION.PERM_EMPLOYEE,
          code: this.uiStore.code,
          token: this.uiStore.token,
        })

        this.uiStore.setSelectedUserId(userId)

        return userId
      }
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('verifyEmployeeError')),
        description: (error as Error).message,
      })
    }
  }

  async updateUserRoles() {
    try {
      const pvzId = this.uiStore.selectedPvz?.id

      if (pvzId) {
        const newRoles = this.uiStore.employeeRoleList.reduce<UpdateUserPvzRolesRequest[]>((acc, item) => {
          if (item.role !== item.cachedRole) {
            acc.push({
              role: item.role,
              employee_id: this.uiStore.selectedUserId,
              pickpoint_id: item.id!,
            })
          }

          return acc
        }, [])

        const result = await employeeApi.updateUserPvzRoles(newRoles)
        const errors: string[] = []

        result.forEach((item, index) => {
          if (!item.status) {
            const pvzAddress = this.uiStore.employeeRoleStore.getEntity(item.pickpointId)
            errors.push(`${index + 1}. ${item.message}\n${pvzAddress?.address}`)
          }
        })

        if (errors.length) {
          notification.error({
            message: i18next.t(getKey('updateUserRolesError')),
            description: errors.join('\n\n'),
          })
        }

        return result
      }
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('updateUserRolesError')),
        description: (error as Error).message,
      })
    }
  }

  async updateUserELockRoles() {
    try {
      const res = await employeeApi.updateUserELockRoles(this.uiStore.eLockRoleList)
      return res.success
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('updateUserRolesError')),
        description: (error as Error).message,
      })
    }
  }

  async changeUserSnils(snils: string, userId: number) {
    try {
      const res = await employeeApi.changeUserSnils(+snils, userId)

      this.uiStore.getEmployeeList()

      return res.success
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('changeUserSnilsError')),
        description: (error as Error).message,
      })
    }
  }

  async deleteUser(userId: number) {
    try {
      const res = await employeeApi.deleteUser(userId)

      this.uiStore.getEmployeeList()

      return res.data
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('deleteUserError')),
        description: (error as Error).message,
      })
    }
  }

  async recoverUser(userId: number) {
    try {
      const res = await employeeApi.recoverUser(userId)

      return res.data
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('recoverUserError')),
        description: (error as Error).message,
      })
    }
  }
}
